/**
 * Pure composition of the umbrella×Ireland sidebar from `umbrellaRoutesConfig`.
 * Kept in its own module (no icon/asset imports) so it is unit-testable
 * without jest needing an asset mapper for `routeConfig.tsx`'s `.png` barrel.
 *
 * `UK_ONLY_UMBRELLA_IDS` names **parent** ids: `AppRoutes/index.tsx` looks up
 * `routeModuleMap` per extracted id and most children have no getter of their
 * own, so dropping only a child id would hide the sidebar entry while leaving
 * the route deep-linkable. Ids that get a Irish replacement instead of a
 * straight drop (`hmrc-update`, `rti-filings-history`, `agency-reports`) are
 * not in this set — they are keyed in `replacements` instead, which takes
 * priority in the flatMap below.
 */
export const UK_ONLY_UMBRELLA_IDS = new Set<string>([
  'statutory-payments',
  'KID',
  'reports/agency/employment-intermediary',
  // The Accounting section is a single Xero journal export. `src/modules/journal`
  // has no Ireland branch at all — the journal lines it posts are ERNI, PAYE and
  // the Apprenticeship Levy, none of which an Irish payroll produces — so the
  // page can only emit a UK-shaped journal for Irish figures.
  'accounting',
]);

const UK_ONLY_REPORT_PATHS = new Set([
  '/reports/payroll/p30',
  '/reports/payroll/p32',
  '/reports/payroll/cis-payment-summary',
  '/reports/management/employment-intermediary',
  '/reports/employee/awr',
  '/reports/employee/right-to-work',
  '/reports/attachment-orders-report',
]);

const IRELAND_ONLY_REPORT_PATHS = new Set([
  '/reports/pension/contributions-ireland',
]);
const WTR_REPORT_PATH = '/reports/employee/wtr';

export function reportRoutesForCountry<T extends { path: string }>(
  routes: T[],
  country: string,
  isBureau: boolean,
): T[] {
  return routes.filter((route) => {
    if (country === 'IRELAND') {
      // Ireland Umbrella retains its WTR report; Ireland Bureau has no WTR menu.
      return (
        !UK_ONLY_REPORT_PATHS.has(route.path) &&
        !(isBureau && route.path === WTR_REPORT_PATH)
      );
    }
    return !IRELAND_ONLY_REPORT_PATHS.has(route.path);
  });
}

interface RouteMenuItem {
  id?: string;
  type?: string;
  [key: string]: unknown;
}

export function composeUmbrellaIrelandRoutes<T extends RouteMenuItem>(
  umbrella: T[],
  replacements: Record<string, T[]>,
): T[] {
  return umbrella.flatMap((item) => {
    if (!item.id) return [item]; // divider — passes through unchanged
    if (item.id in replacements) return replacements[item.id];
    if (UK_ONLY_UMBRELLA_IDS.has(item.id)) return [];
    return [item];
  });
}
